import { DependencyList, useEffect, useState } from 'react'

import { AsyncEffectHandle, useAsyncEffectGenerator } from './async-effect'
import TableControl from '../models/TableControl'

/**
 * Provide a paginated table with controls for sorting and filtering.
 * This hook assumes the given effect will initialize the TableControl and provide necessary model typing.
 * At this stage, we are not interested in working with the DTO any more,
 *  because the model mapping happens inside the TableControl class.
 */
export const usePaginatedTable = <T>(
    effect: (table: TableControl<T>) => Promise<TableControl<T> | undefined | null>,
    deps: DependencyList
): [TableControl<T>, ...AsyncEffectHandle] => {
    const [table, setTable] = useState(TableControl.empty<T>())

    const [loading, error, refresh] = useAsyncEffectGenerator(async () => {
        const updated = await effect(table)
        setTable(updated ?? TableControl.empty<T>())
        return updated
    })

    useEffect(() => {
        refresh()
    }, deps)

    // Assign the refresh function to the TableControl such that it can invoke a render cycle when it changes its internal state.
    // This removes all necessity of handling refreshing functions here, and instead lets the provided TableControl model do this on its own.
    // NOTE: This is a bit of an anti-pattern and requires the TableControl to call the refresh function internally, but makes it much easier to implement.
    table.refresh = refresh

    return [table, loading, error, refresh]
}
